import moment from "moment";
import { Button, message, Modal } from "antd";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { RootState } from "../../redux/rootReducer";
import { createInvoice } from "../../redux/actions/invoiceActions";
import { Currency, InvoiceStatus } from "../../shared/constant";
import dateUtils from "../../utils/dateUtils";

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
  form: any;
  filterForm: any;
  client: any;
}

function CreateInvoiceModal({
  visible,
  onCancel,
  form,
  filterForm,
  client,
}: ModalFormProps) {
  const dispatch = useDispatch();
  const { tracksIncomeSummary, allTracks } = useSelector(
    (state: RootState) => state.track,
  );
  const { currencies } = useSelector((state: RootState) => state.currency);
  const { pending, createInvoiceSuccess } = useSelector(
    (state: RootState) => state.invoice,
  );
  const { allLabels } = useSelector((state: RootState) => state.label);
  const { allArtists, allLyricists, allComposers } = useSelector(
    (state: RootState) => state.contributor,
  );
  const { allPlatforms } = useSelector((state: RootState) => state.platform);
  const { allDistributors } = useSelector(
    (state: RootState) => state.distributor,
  );

  const [fields] = useState(form.getFieldsValue());
  const [filterFields] = useState(filterForm.getFieldsValue());

  const getCurrencyID = () => {
    const c = currencies.find((item: any) => item.name === fields.currency);
    return c.id;
  };

  // TODO REUSE THIS: also present in ClapIncomes
  const getPayableAmount = () => {
    if (!tracksIncomeSummary) return 0;

    switch (fields.currency) {
      case Currency.BDT:
        return tracksIncomeSummary.total_payable_bdt;
      case Currency.EUR:
        return tracksIncomeSummary.total_payable_eur;
      case Currency.USD:
        return tracksIncomeSummary.total_payable_usd;

      default:
        return "";
    }
  };

  const getInvoiceTitle = () => {
    return `${client.name} ${fields.income_period
      ? `(${dateUtils.defaultFormat(
        fields.income_period[0],
      )} ~ ${dateUtils.defaultFormat(fields.income_period[1])})`
      : ""
      } ${fields.currency ? `- ${fields.currency}` : ""}`;
  };

  const getNameFromFilter = (
    optionList: any,
    selectedOption: any,
    type: string,
  ) => {
    if (!selectedOption) return "";
    const filteredNames = optionList
      .filter((label: any) => selectedOption.includes(label.id))
      .map((label: any) => (type === "Track" ? label.title : label.name))
      ?.join(", ");

    return filteredNames;
  };

  useEffect(() => {
    if (createInvoiceSuccess) {
      onCancel();
      message.success("Invoice created successfully");
    }
  }, [pending]);

  const handleCreate = async () => {
    const payload = {
      title: getInvoiceTitle(),
      client: client.id,
      ...(fields.income_period && {
        from_date: moment(fields.income_period[0]).format(),
        to_date: moment(fields.income_period[1]).format(),
      }),
      currency: getCurrencyID(),
      invoice_amount: parseFloat(getPayableAmount()).toFixed(3),
      status: InvoiceStatus.DRAFT,
      labels_filter: getNameFromFilter(allLabels, filterFields.labels, "Label"),
      artists_filter: getNameFromFilter(
        allArtists,
        filterFields.artist,
        "Artist",
      ),
      lyricists_filter: getNameFromFilter(
        allLyricists,
        filterFields.lyricist,
        "Lyricist",
      ),
      composers_filter: getNameFromFilter(
        allComposers,
        filterFields.composer,
        "Composer",
      ),
      tracks_filter: getNameFromFilter(allTracks, filterFields.track, "Track"),
      platforms_filter: getNameFromFilter(
        allPlatforms,
        filterFields.platform,
        "Platform",
      ),
      distributors_filter: getNameFromFilter(
        allDistributors,
        filterFields.distributor,
        "Distributor",
      ),
      dues: tracksIncomeSummary ? [...tracksIncomeSummary.dues] : [],
    };
    // console.log("CREATE INVOICE PAYLOAD", payload);
    dispatch(createInvoice(payload));
  };

  return (
    <Modal
      visible={visible}
      title="Create Invoice"
      centered
      width={740}
      onOk={handleCreate}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={pending as boolean}
          onClick={handleCreate}
          disabled={getPayableAmount() === 0}
        >
          Create
        </Button>,
      ]}
    >
      <div className="mx-6 mt-6 mb-[14px]">
        <div className="grid grid-cols-2 gap-x-6 mb-4">
          <div>
            <div className="text-black opacity-70">Invoice Title</div>
            <div>{getInvoiceTitle()}</div>
          </div>
          <div>
            <div className="text-black opacity-70">Income Period</div>
            {fields.income_period && (
              <div>
                {dateUtils.defaultFormat(fields.income_period[0])} ~{" "}
                {dateUtils.defaultFormat(fields.income_period[1])}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 my-4">
          <div>
            <div className="text-black opacity-70">Client</div>
            <div>{client.name}</div>
          </div>
          <div>
            <div className="text-black opacity-70">Currency</div>
            <div>{fields.currency}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6">
          <div>
            <div className="text-black opacity-70">Payable Amount</div>
            <div>
              {fields.currency && parseFloat(getPayableAmount()).toFixed(3)}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CreateInvoiceModal;
